import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { DatePicker, Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import Sidebar from "./Sidebar";
import { useAuth } from "../context/AuthContext";
import "./styles/Dashboard.css";
// PHASE 2B: Import unified campaign data hooks
import { useDashboardSummary, useDashboardGraph, useInvalidateCampaigns } from "../hooks/useCampaignData";
import { useSubscription } from "../hooks/useSubscription";
import { Progress } from "antd";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [summary, setSummary] = useState({
    totalCampaigns: 0,
    totalEmailsSent: 0,
    activeCampaigns: 0,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(""); // State for real-time clock
  const [graphData, setGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, 'days'),
    moment()
  ]);
  
  const { RangePicker } = DatePicker;
  
  // PHASE 2B: Use unified campaign data hooks for instant dashboard loads
  const { 
    data: cachedSummary, 
    isLoading: summaryLoading, 
    error: summaryError,
    refetch: refetchSummary
  } = useDashboardSummary(user?.uid, { enabled: !!user });
  
  const { 
    data: cachedGraphData, 
    isLoading: cachedGraphLoading,
    refetch: refetchGraph
  } = useDashboardGraph(user?.uid, dateRange, { enabled: !!user });
  
  // PHASE 2B: Manual refresh functionality
  const invalidateCampaigns = useInvalidateCampaigns();
  
  // ✅ NEW: Fetch subscription data for usage widget
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription(user?.uid, { enabled: !!user });
  
  const handleManualRefresh = () => {
    console.log('🔄 [Phase 2B] Manual refresh triggered from Dashboard');
    // Invalidate cache first, then refetch
    if (user?.uid) {
      invalidateCampaigns(user.uid);
    }
    refetchSummary();
    refetchGraph();
  };

  // ✅ PERFORMANCE: Update summary state when cached data changes
  useEffect(() => {
    if (cachedSummary) {
      const summary = {
        totalCampaigns: Number(cachedSummary.totalCampaigns) || 0,
        totalEmailsSent: Number(cachedSummary.totalEmailsSent) || 0,
        activeCampaigns: Number(cachedSummary.activeCampaigns) || 0,
      };
      
      console.log('📦 [Cache] Dashboard summary loaded:', summary);
      setSummary(summary);
      setLoading(false);
      setError(null);
    }
  }, [cachedSummary]);
  
  // ✅ PERFORMANCE: Update loading state from React Query
  useEffect(() => {
    setLoading(summaryLoading);
  }, [summaryLoading]);
  
  // ✅ Handle dashboard errors
  useEffect(() => {
    if (summaryError) {
      console.error("❌ Dashboard: Error fetching summary:", summaryError);
      setError(summaryError.message || "Failed to fetch dashboard data");
      setLoading(false);
      
      if (summaryError.response?.status === 401) {
        navigate("/login");
      }
    }
  }, [summaryError, navigate]);

  // ✅ PERFORMANCE: Update graph data when cached data changes
  useEffect(() => {
    if (cachedGraphData?.data) {
      // Transform the data to ensure all fields are present
      const transformedData = cachedGraphData.data.map(item => ({
        date: item.date,
        fullDate: item.fullDate || item.date,
        campaignsCreated: item.campaignsCreated || 0,
        emailsSent: item.emailsSent || 0,
      }));
      
      // Filter out any invalid data points
      const validData = transformedData.filter(item => 
        item.date && item.fullDate && 
        !isNaN(item.campaignsCreated) && 
        !isNaN(item.emailsSent)
      );
      
      console.log(`📦 [Cache] Dashboard graph loaded: ${validData.length} data points`);
      setGraphData(validData);
      setGraphLoading(false);
    }
  }, [cachedGraphData]);
  
  // ✅ PERFORMANCE: Update graph loading state from React Query
  useEffect(() => {
    setGraphLoading(cachedGraphLoading);
  }, [cachedGraphLoading]);

  // Update real-time clock
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleString());
    };
    updateClock(); // Initial call
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
      // ✅ PERFORMANCE: React Query will automatically refetch when dateRange changes
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              <h2 style={{ margin: 0 }}>Dashboard</h2>
            </div>
            {/* PHASE 2B: Manual refresh button - Only show when there's an error or data hasn't loaded */}
            {(error || summaryError) && (
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={summaryLoading || cachedGraphLoading}
                type="primary"
                size="large"
              >
                Refresh Data
              </Button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span>⚠️ {error}</span>
            {/* ✅ React Query will automatically retry - manual retry not needed */}
          </div>
        )}

        {loading ? (
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>Loading dashboard data...</p>
          </div>
        ) : (
          <>
            <div className="dashboard-cards">
              <div className="card-item">
                <div className="card-number">{summary.totalCampaigns}</div>
                <div className="card-title">Total Campaigns</div>
              </div>
              <div className="card-item">
                <div className="card-number">{summary.totalEmailsSent}</div>
                <div className="card-title">Total Emails Sent</div>
              </div>
              <div className="card-item">
                <div className="card-number">{summary.activeCampaigns}</div>
                <div className="card-title">Active Campaigns</div>
              </div>
            </div>

            {/* ✅ NEW: Full-Width Subscription Usage Widget */}
            {subscription && subscription.status === 'active' && !subscriptionLoading && (
              <div className="dashboard-graph" style={{ marginBottom: '2rem' }}>
                <div className="card-number">
                  {subscription.plan?.charAt(0).toUpperCase() + subscription.plan?.slice(1).toLowerCase()} Plan
                </div>
                <div className="card-title" style={{ marginBottom: '0.8rem' }}>
                  {(subscription.monthlyUsage || 0).toLocaleString()} / {(subscription.monthlyLimit || 0).toLocaleString()}
                </div>
                
                <Progress 
                  percent={subscription.usagePercentage || 0}
                  showInfo={false}
                  status={subscription.usagePercentage > 90 ? 'exception' : 'active'}
                  style={{
                    marginBottom: '0.8rem'
                  }}
                />
                
                <div className="card-title">
                  {(subscription.remainingContacts || 0).toLocaleString()} Contacts Remaining
                  {subscription.resetDateMillis && (
                    <div style={{ marginTop: '4px', textAlign: 'center' }}>
                      Resets: {new Date(subscription.resetDateMillis).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="dashboard-graph">
              <div className="graph-header">
                <h3>Daily Performance Overview</h3>
                <div className="date-range-picker">
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    allowClear={false}
                    disabledDate={(current) => current && current > moment().endOf('day')}
                    style={{ marginLeft: '20px' }}
                    placeholder={['Start Date', 'End Date']}
                  />
                </div>
              </div>
              
              {graphLoading ? (
                <div className="graph-loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading graph data...</p>
                </div>
              ) : graphData.length === 0 ? (
                <div className="graph-empty-state">
                  <div className="empty-state-icon">📊</div>
                  <h4>No activity found</h4>
                  <p>No activity found for the selected date range.</p>
                  <p>Try selecting a different date range or create some campaigns to see data here.</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart
                    data={graphData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      yAxisId="campaigns" 
                      orientation="left"
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Campaigns Created', angle: -90, position: 'insideLeft' }}
                    />
                    <YAxis 
                      yAxisId="emails" 
                      orientation="right"
                      tick={{ fontSize: 12 }}
                      label={{ value: 'Emails Sent', angle: 90, position: 'insideRight' }}
                    />
                    <Tooltip 
                      labelFormatter={(value, payload) => {
                        if (payload && payload[0] && payload[0].payload) {
                          return `📅 ${payload[0].payload.fullDate}`;
                        }
                        return `📅 ${value}`;
                      }}
                      formatter={(value, name) => {
                        const icon = name === 'Emails Sent' ? '📧' : '📋';
                        return [`${icon} ${name}: ${value}`, ''];
                      }}
                      separator=""
                      labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        padding: '8px'
                      }}
                    />
                    <Legend 
                      formatter={(value) => {
                        const icon = value === 'Emails Sent' ? '🔵' : '🟢';
                        return `${icon} ${value}`;
                      }}
                      wrapperStyle={{ paddingTop: '10px' }}
                    />
                    <Line
                      yAxisId="campaigns"
                      type="monotone"
                      dataKey="campaignsCreated"
                      stroke="#82ca9d"
                      strokeWidth={2}
                      name="Campaigns Created"
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="emails"
                      type="monotone"
                      dataKey="emailsSent"
                      stroke="#8884d8"
                      strokeWidth={2}
                      name="Emails Sent"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
